import { motion } from "framer-motion";
import { Quote } from "lucide-react";

export const AboutUs = () => {
  const unpluggedBenefits = [
    "Visualize abstract concepts",
    "Critical thinking with persistence",
    "Nurture creative expression",
    "Logical problem solving",
    "Develop fluid thinking",
  ];

  const futureSkills = [
    "Conceptual ideas on the creative and imaginative front",
    "Tap into innate intuitive powers",
    "Insight to see the bigger picture",
    "Enhance social empathy and emotional quotient",
  ];

  return (
    <div className="w-full bg-white">
      {/* Top Hero Section with Background Image */}
      <section className="relative h-[500px] flex items-center justify-center overflow-hidden">
        {/* Background Image with Overlay */}
        <div 
          className="absolute inset-0 bg-cover bg-center bg-no-repeat"
          style={{
            backgroundImage: "url('https://images.unsplash.com/photo-1522071820081-009f0129c71c?q=80&w=2070')",
          }}
        >
          <div className="absolute inset-0 bg-linear-to-r from-gray-900/80 via-gray-900/70 to-gray-900/60" />
        </div>
        
        {/* Content */}
        <motion.div 
          initial={{ opacity: 0, y: 30 }} 
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8 }}
          className="relative z-10 max-w-7xl mx-auto px-4 text-center"
        >
          <h2 className="text-primary text-xl md:text-2xl font-medium mb-4 tracking-wide">
            About us
          </h2>
          <h1 className="text-white text-4xl md:text-6xl lg:text-7xl font-bold leading-tight">
            We Teach Coding Unplugged
          </h1>
          <p className="text-white/90 text-lg md:text-xl mt-6 max-w-3xl mx-auto">
            Code Riite focuses on the basic concepts of coding that are fundamental to learning to code. We provide hands-on, engaging learning experiences that encourage children to think like a coder.
          </p>
        </motion.div>
      </section>

      {/* Hero */}
      <section className="relative overflow-hidden bg-linear-to-br from-orange-50 via-white to-orange-100">
        <div className="max-w-7xl mx-auto px-4 py-20 grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
          <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }}>
            <h1 className="text-4xl md:text-5xl font-bold text-gray-900 mt-3">
              Building computational minds through joyful, screen-free learning
            </h1>
            <p className="text-lg text-gray-700 mt-6 leading-relaxed">
              We at Code Riite aspire to instill the 21st-century skills of programming and computational thinking
              within young minds through our fun and intriguing curriculum.
            </p>
            <p className="text-lg text-gray-700 mt-4 leading-relaxed">
              Children are introduced to a fascinating dimension of technology, empowering them to become the
              architects of the digital era. At Code Riite, coding for kids is an unplugged, gamified activity class
              with an edge of computational thinking.
            </p>
          </motion.div>
          <motion.div
            initial={{ opacity: 0, scale: 0.95 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ delay: 0.1 }}
            className="relative"
          >
            <div className="absolute -top-6 -left-6 w-24 h-24 bg-primary/10 rounded-full blur-2xl" />
            <div className="absolute -bottom-8 -right-6 w-32 h-32 bg-secondary/10 rounded-full blur-2xl" />
            <img
              src="https://img.freepik.com/free-vector/kids-learning-programming-concept-illustration_114360-1652.jpg"
              alt="Kids learning coding together"
              className="relative w-full h-auto rounded-3xl shadow-2xl border border-white"
            />
          </motion.div>
        </div>
      </section>

      {/* Coding */}
      <section className="py-20 px-4">
        <div className="max-w-7xl mx-auto grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
          <div className="order-2 lg:order-1">
            <img
              src="https://img.freepik.com/free-vector/kids-creating-robot-with-programming-laptop_23-2148524610.jpg"
              alt="Kids exploring creative coding"
              className="w-full h-auto rounded-3xl shadow-xl"
            />
          </div>
          <div className="order-1 lg:order-2">
            <span className="text-primary font-bold tracking-wider uppercase text-sm">We Teach Coding Unplugged</span>
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mt-3">Creativity before technology</h2>
            <p className="text-lg text-gray-700 mt-6 leading-relaxed">
              Coding is the set of instructions we create to communicate with computers. Through coding, we make
              computers perform tasks.
            </p>
            <p className="text-lg text-gray-700 mt-4 leading-relaxed">
              For kids, the goal is creativity rather than technology. Coding for children as early as age 5 exposes
              them to logical thinking, reasoning skills, pattern recognition, self-discovery, problem solving, and
              intrinsic motivation.
            </p>
          </div>
        </div>
      </section>

      {/* Coding Unplugged */}
      <section className="py-20 px-4 bg-gray-50">
        <div className="max-w-7xl mx-auto grid grid-cols-1 lg:grid-cols-2 gap-12 items-start">
          <div>
            <span className="text-primary font-bold tracking-wider uppercase text-sm">We Teach Coding Unplugged</span>
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mt-3">Conceptualizing, not just programming</h2>
            <p className="text-lg text-gray-700 mt-6 leading-relaxed">
              Code Riite focuses on the basic concepts of coding that are fundamental to learning to code. We provide
              hands-on, engaging learning experiences that encourage children to think like a coder.
            </p>
            <p className="text-lg text-gray-700 mt-4 leading-relaxed">
              Our curriculum includes creative unplugged coding activities that help children learn screen-free.
              Exercising these activities fosters resilience, creative problem solving, critical thinking, and logical
              reasoning.
            </p>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            {unpluggedBenefits.map((item) => (
              <div
                key={item}
                className="bg-white rounded-2xl p-5 shadow-sm border border-gray-100 hover:shadow-md transition-shadow"
              >
                <p className="text-gray-800 font-semibold">{item}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Future Skills */}
      <section className="py-20 px-4">
        <div className="max-w-7xl mx-auto grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
          <div>
            <span className="text-primary font-bold tracking-wider uppercase text-sm">Future Ready</span>
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mt-3">Our future needs these abilities</h2>
            <div className="mt-8 grid grid-cols-1 sm:grid-cols-2 gap-4">
              {futureSkills.map((item) => (
                <div key={item} className="bg-orange-50 rounded-2xl p-5 border border-orange-100">
                  <p className="text-gray-800 font-medium">{item}</p>
                </div>
              ))}
            </div>
          </div>
          <div>
            <img
              src="https://img.freepik.com/free-vector/people-analyzing-growth-charts_23-2148866843.jpg"
              alt="Future skills and growth"
              className="w-full h-auto rounded-3xl shadow-xl"
            />
          </div>
        </div>
      </section>

      {/* Founder Section */}
      <section className="py-24 px-4 bg-white overflow-hidden">
        <div className="max-w-6xl mx-auto">
          <div className="text-center mb-16">
            {/* <span className="text-primary font-bold tracking-wider uppercase text-sm">Leadership</span> */}
            <h2 className="text-4xl font-bold text-gray-900 mt-2">Meet the Founder</h2>
          </div>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="relative bg-gray-50 rounded-4xl p-8 md:p-12 shadow-xl"
          >
            <div className="absolute top-0 right-0 -mt-10 -mr-10 w-64 h-64 bg-primary/5 rounded-full blur-3xl" />
            <div className="absolute bottom-0 left-0 -mb-10 -ml-10 w-64 h-64 bg-secondary/5 rounded-full blur-3xl" />

            <div className="relative flex flex-col md:flex-row items-center gap-12">
              <div className="w-full md:w-1/3 flex justify-center">
                <div className="relative">
                  <div className="absolute inset-0 bg-linear-to-tr from-primary to-secondary rounded-full blur-sm opacity-50 transform translate-y-2" />
                  <img
                    src="/images/Aashna-Shah-Code-Right-200x200.jpg"
                    alt="Aashna Shah"
                    className="relative w-64 h-64 object-cover rounded-full border-4 border-white shadow-lg z-10"
                  />
                  <div className="absolute -bottom-4 -right-4 bg-white p-3 rounded-full shadow-lg z-20">
                    <Quote className="w-6 h-6 text-primary fill-current" />
                  </div>
                </div>
              </div>

              <div className="w-full md:w-2/3 text-center md:text-left">
                <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-4">
                  <div>
                    <h3 className="text-3xl font-bold text-gray-900">Aashna Shah</h3>
                    <p className="text-secondary font-medium text-lg">Founder & Lead Educator</p>
                  </div>
                  <div className="flex flex-wrap justify-center md:justify-start gap-2">
                    <span className="px-3 py-1 rounded-full bg-white text-gray-700 text-xs font-semibold shadow-sm border border-gray-100">
                      10+ years teaching
                    </span>
                    <span className="px-3 py-1 rounded-full bg-white text-gray-700 text-xs font-semibold shadow-sm border border-gray-100">
                      NMIMS & VIT faculty
                    </span>
                  </div>
                </div>

                <p className="text-gray-600 text-lg leading-relaxed mt-6">
                  Aashna Shah leads Code Riite with a focus on helping young learners build strong logical foundations
                  through computational thinking and screen-free coding.
                </p>
                <p className="text-gray-600 text-lg leading-relaxed mt-4">
                  With over a decade in Computer Science and IT education, she brings deep academic and practical
                  expertise, including experience as engineering faculty at NMIMS and VIT.
                </p>
                <p className="text-gray-600 text-lg leading-relaxed mt-4">
                  Her approach turns complex concepts into playful, hands-on activities that teach children how to
                  think, reason, and solve problems with confidence.
                </p>
                <div className="mt-6 rounded-2xl bg-white/80 border border-gray-100 p-5 shadow-sm">
                  <p className="text-xs uppercase tracking-wider text-gray-500 font-semibold">Teaching philosophy</p>
                  <p className="text-gray-700 italic mt-2">
                    "Coding is not just about writing code. It is about learning how to think."
                  </p>
                </div>

                <div className="mt-8">
                  <a
                    href="/about#founder"
                    className="inline-flex items-center justify-center rounded-full bg-primary px-6 py-2 text-sm font-semibold text-white shadow-md hover:bg-orange-700 transition-colors"
                  >
                    Read more
                  </a>
                </div>
              </div>
            </div>
          </motion.div>
        </div>
      </section>
    </div>
  );
};

export default AboutUs;
